/**
 * Submission Module
 * Form AJAX submission and response handling
 */

/**
 * Request options for submitRequest
 */
export interface SubmitRequestOptions {
    url: string;
    method?: string;
    data?: FormData | string | Record<string, unknown>;
    contentType?: string;
    timeout?: number;
    hasFiles?: boolean;
    dataType?: 'json' | 'text';
}

/**
 * Error response from failed request
 */
export interface SubmitErrorResponse {
    responseJSON?: Record<string, string> | null;
    responseText?: string;
    status?: number;
}

/**
 * Success response structure
 */
export interface SuccessResponse {
    success?: boolean;
    message?: string;
    [key: string]: unknown;
}

/**
 * MultiSelect element with instance reference
 */
interface MultiSelectElement extends HTMLSelectElement {
    multiSelectInstance?: {
        clear(triggerChange?: boolean): void;
    };
}

/**
 * Serializes form data for submission.
 */
export function serializeForm(form: HTMLFormElement, hasFiles: boolean = false): FormData | string {
    const formData = new FormData(form);

    if (hasFiles) {
        return formData;
    }

    // Convert FormData to URL-encoded string
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
        if (typeof value === 'string') {
            params.append(key, value);
        }
    });

    return params.toString();
}

/**
 * Submits an AJAX request using the Fetch API with timeout support.
 */
export async function submitRequest<T = unknown>(options: SubmitRequestOptions): Promise<T> {
    const {
        url,
        method = 'POST',
        data,
        contentType = 'application/x-www-form-urlencoded; charset=UTF-8',
        timeout = 60000,
        hasFiles = false,
        dataType = 'json'
    } = options;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
        controller.abort();
    }, timeout);

    const headers = new Headers();
    headers.append('X-Requested-With', 'XMLHttpRequest');

    const fetchOptions: RequestInit = {
        method: method.toUpperCase(),
        signal: controller.signal,
        credentials: 'same-origin',
        headers: headers
    };

    if (fetchOptions.method !== 'GET') {
        if (hasFiles || data instanceof FormData) {
            fetchOptions.body = data as FormData;
        } else {
            headers.append('Content-Type', contentType);

            if (contentType.includes('application/json')) {
                fetchOptions.body = JSON.stringify(data);
            } else {
                fetchOptions.body = data as string;
            }
        }
    }

    try {
        const response = await fetch(url, fetchOptions);
        window.clearTimeout(timeoutId);

        if (response.ok === false) {
            const errorData = await response.json().catch(() => {
                return null;
            });

            throw {
                responseJSON: errorData,
                responseText: response.statusText,
                status: response.status
            } as SubmitErrorResponse;
        }

        if (dataType === 'json') {
            return await response.json() as T;
        }

        const textData = await response.text();
        return textData as unknown as T;

    } catch (error) {
        window.clearTimeout(timeoutId);
        throw error;
    }
}

/**
 * Refreshes the CSRF token for all forms on the page.
 */
export function refreshCsrfToken(): void {
    fetch('/refresh-csrf')
        .then((response) => response.text())
        .then((data) => {
            document.querySelectorAll<HTMLInputElement>('input[name="_token"]').forEach((input) => {
                input.value = data;
            });
        })
        .catch(() => {
            // Silently fail - CSRF refresh is not critical
        });
}

/**
 * Resets all form fields and validation states.
 */
export function resetFormFields(form: HTMLFormElement, submitBtn: HTMLButtonElement | null = null): void {
    const formFields = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        'input[type="text"], input[type="email"], textarea, input[type="file"], input[type="tel"], input[type="password"], select'
    );

    formFields.forEach((field) => {
        field.value = '';
    });

    form.querySelectorAll<HTMLElement>('.is-valid, .is-invalid').forEach((el) => {
        el.classList.remove('is-valid', 'is-invalid');
    });

    form.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
    });

    form.querySelectorAll<HTMLInputElement>('input[type="file"]').forEach((fileInput) => {
        fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (submitBtn) {
        submitBtn.disabled = true;
    }

    form.querySelectorAll<MultiSelectElement>('.custom-multiselect').forEach((el) => {
        if (el.multiSelectInstance) {
            el.multiSelectInstance.clear(false);
        }
    });
}

/**
 * Logical check to determine if the server response represents a "Success".
 */
export function isSuccessResponse(response: SuccessResponse | SuccessResponse[] | null): boolean {
    if (response === null) {
        return false;
    }

    let responseObj: SuccessResponse;

    if (Array.isArray(response)) {
        if (response.length === 0) {
            return false;
        }
        responseObj = response[0];
    } else {
        responseObj = response;
    }

    if (typeof responseObj.success !== 'undefined') {
        return responseObj.success === true;
    }

    // Fallback: If no success flag, treat presence of a message as an error,
    // or absence of message as success.
    return typeof responseObj.message === 'undefined';
}

/**
 * Injects conversion tracking pixels dynamically.
 */
export function fireLinkedInPixel(componentId: string | null, linkedinPixel: string | null): void {
    if (linkedinPixel && componentId) {
        const pixelContainer = document.getElementById(`mp-component-event-pixel-${componentId}`);

        if (pixelContainer) {
            pixelContainer.innerHTML = linkedinPixel;
        }
    }
}